import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import React, { useEffect, useState, useMemo } from "react"
import { Card, CardBody, Container, Table } from "reactstrap"
import moment from "moment"
import { DatePicker } from "antd"
import { fetchData, FetchPostData } from "utils/Common_api_functions"
import { formatAnyTime } from "utils/commonFunctions"
import reportIMG from "../../assets/images/report1.png"
import noreportIMG from "../../assets/images/noReport.jpg"
import PermittedComponent from "PermittedComponent"
import { usePermissionData } from "AppContext"

const { RangePicker } = DatePicker

const EmployeeAllocationReport = () => {
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState([])
  const [selectedLocation, setSelectedLocation] = useState("")
  const [selectedEmployee, setSelectedEmployee] = useState("")

  const [allData, setAllData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [locationsList, setLocationsList] = useState([])
  const [employeesList, setEmployeesList] = useState([])

  useEffect(() => {
    fetchLocations()
    fetchEmployees()
  }, [])

  useEffect(() => {
    if (selectedEmployee || selectedLocation || (dateRange?.length === 2 && dateRange[0] && dateRange[1])) {
      getReportData()
    } else {
      setAllData([])
      setFilteredData([])
    }
  }, [dateRange, selectedLocation, selectedEmployee])

  async function fetchLocations() {
    const res = await fetchData(setLoading, "location")
    if (res?.success) {
      setLocationsList(res.data || [])
    }
  }

  async function fetchEmployees() {
    const res = await fetchData(setLoading, "employee")
    if (res?.success) {
      setEmployeesList(res.data || [])
    }
  }

  async function getReportData() {
    const payload = {
      start_date: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : undefined,
      end_date: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : undefined,
      location_id: selectedLocation || undefined,
      employee_id: selectedEmployee || undefined,
    }

    const APIdata = await FetchPostData(
      setLoading,
      "employee/allocation/report",
      payload
    )

    if (APIdata?.data?.success) {
      setAllData(APIdata?.data?.data || [])
      setFilteredData(APIdata?.data?.data || [])
    } else {
      setAllData([])
      setFilteredData([])
    }
  }

  useEffect(() => {
    let data = allData

    if (selectedLocation) {
      data = data.filter(item => item?.locationId === Number(selectedLocation))
    }

    if (selectedEmployee) {
      data = data.filter(item => item?.employeeId === Number(selectedEmployee))
    }

    setFilteredData(data)
  }, [selectedLocation, selectedEmployee, allData])

  const locationOptions = useMemo(() => {
    if (selectedEmployee) {
      const locIds = new Set(
        allData
          .filter(item => item.employeeId === Number(selectedEmployee))
          .map(item => item.locationId)
      )
      const filtered = locationsList.filter(loc => locIds.has(loc.id))
      return filtered.length > 0 ? filtered : locationsList
    }
    return locationsList
  }, [allData, locationsList, selectedEmployee])

  const employeeOptions = useMemo(() => {
    if (selectedLocation) {
      const empIds = new Set(
        allData
          .filter(item => item.locationId === Number(selectedLocation))
          .map(item => item.employeeId)
      )
      const filtered = employeesList.filter(emp => empIds.has(emp.id))
      return filtered.length > 0 ? filtered : employeesList
    }
    return employeesList
  }, [allData, employeesList, selectedLocation])

  const { checkAccess } = usePermissionData()

  const hasDateRange = dateRange?.length === 2 && dateRange[0] && dateRange[1]
  const hasAnyFilter = selectedEmployee || selectedLocation || hasDateRange

  const handleDateRangeChange = dates => {
    setDateRange(dates || [])
  }

  return (
    <div className="page-content">
      <PermittedComponent isAccess={checkAccess(8, 1) || checkAccess(8, 2)}>
        {loading && <Loader />}
        <Container fluid>
          <Breadcrumb
            title={"Reports"}
            breadcrumbItem={"Employee Allocation Report"}
            description={"View and Check Employee Allocation Reports"}
          />

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-5 flex-wrap gap-3">
                <h4>Employee Allocation Reports</h4>
                <div className="d-flex gap-3 flex-wrap align-items-end">
                  <div>
                    <h6>Select Employee</h6>
                    <select
                      className="form-select w-auto rounded-4 py-2 pe-5"
                      value={selectedEmployee}
                      onChange={e => setSelectedEmployee(e.target.value)}
                    >
                      <option value="">All Employees</option>
                      {employeeOptions.map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <h6>Select Location</h6>
                    <select
                      className="form-select w-auto rounded-4 py-2 pe-5"
                      value={selectedLocation}
                      onChange={e => setSelectedLocation(e.target.value)}
                    >
                      <option value="">All Locations</option>
                      {locationOptions.map(loc => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <h6>Select Date Range</h6>
                    <RangePicker
                      value={dateRange}
                      onChange={handleDateRangeChange}
                      format="DD/MM/YYYY"
                      allowClear
                      className="w-100"
                      style={{
                        height: "38px",
                        borderRadius: "6px",
                      }}
                    />
                  </div>
                </div>
              </div>

              {!hasAnyFilter && (
                <div className="d-flex flex-column align-items-center">
                  <img src={reportIMG} height={350} alt="reports ready" />
                  <h4>Employee Allocation Reports are Ready.</h4>
                  <p>Select any filter (Employee, Location, or Date Range) to View Reports.</p>
                </div>
              )}

              {hasAnyFilter && filteredData?.length === 0 && (
                <div className="d-flex flex-column align-items-center">
                  <img src={noreportIMG} height={350} alt="no data" />
                  <h5>No Data Found for Selected Filters.</h5>
                </div>
              )}

              {hasAnyFilter && filteredData?.length > 0 && (
                <Table responsive bordered>
                  <thead className="bg-blue">
                    <tr>
                      <th>Sr No.</th>
                      <th>Location Name</th>
                      <th>Employee Name</th>
                      <th>Date</th>
                      <th>Allocated Time</th>
                      <th>Actual Time</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData?.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item?.location?.name}</td>
                        <td>{item?.employee?.name}</td>
                        <td>
                          {item?.date
                            ? moment(item.date).format("DD/MM/YYYY")
                            : "--"}
                        </td>
                        <td>
                          {formatAnyTime(item?.inTime)} -{" "}
                          {formatAnyTime(item?.outTime)}
                        </td>
                        <td>
                          {item?.actualInTime && item?.actualOutTime
                            ? `${formatAnyTime(item?.actualInTime)} - ${formatAnyTime(item?.actualOutTime)}`
                            : "--"}
                        </td>
                        <td>
                          <span
                            className={`badge bg-${
                              item?.actualStatus === "Completed"
                                ? "success"
                                : item?.actualStatus === "Pending"
                                ? "danger"
                                : item?.actualStatus
                                ? "warning"
                                : "secondary"
                            } p-2 px-3 rounded-4`}
                          >
                            {item?.actualStatus || "Not Punched"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Container>
      </PermittedComponent>
    </div>
  )
}

export default EmployeeAllocationReport
